'use client'

import { useEffect, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { z } from 'zod/v4'

import type { RenderLink } from '@ui/web/utils'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
  toast,
} from '@ui/web/components'
import { tryCatch } from '@helper/utils/core'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'
import { AreYouSureDialog, SchoolClassSelect } from '../lib'

const formSchema = z.object({
  childIds: z.array(z.string()).min(1, 'Pilih setidaknya satu anak'),
})

export type SentCollectionToChildFormValues = z.infer<typeof formSchema>

export interface SentCollectionToChildRecipient {
  id: string
  name: string
  image?: string | null
}

interface SentCollectionToChildFormProps {
  myBookCollectionId: string
  onSuccess: () => void
  onCancel: () => void
  /** Optional child user IDs to pre-select when the form loads */
  initialRecipients?: string[]
  /** When true, show AreYouSureDialog on Batal when there are unsaved changes. Default: false */
  withAreYouSureDialog?: boolean
  renderMakeNewChildLink: RenderLink
}

export function SentCollectionToChildForm({
  myBookCollectionId,
  onSuccess,
  onCancel,
  initialRecipients = [],
  withAreYouSureDialog = false,
  renderMakeNewChildLink,
}: SentCollectionToChildFormProps) {
  const trpc = useSharedTRPC()
  const [searchTerm, setSearchTerm] = useState('')
  const [teamId, setTeamId] = useState<string | null>(null)
  const [openAreYouSure, setOpenAreYouSure] = useState(false)

  const {
    data: childrenData,
    isPending: isPendingChildren,
    isError: isErrorChildren,
    refetch: refetchChildren,
  } = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({ pageSize: 'all', teamId: teamId ?? undefined }),
    placeholderData: (prev) => prev,
  })

  const recipients: SentCollectionToChildRecipient[] =
    childrenData?.data.map((c) => ({ id: c.id, name: c.name, image: c.image })) ?? []

  const { mutateAsync: createSentBookCollection } = useMutation(
    trpc.myBookCollection.createSentBookCollection.mutationOptions(),
  )

  const form = useForm({
    defaultValues: {
      childIds: initialRecipients,
    } as z.input<typeof formSchema>,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, err] = await tryCatch(
        createSentBookCollection({
          myBookCollectionId,
          recipientUserIds: value.childIds,
        }),
      )

      if (err) {
        toast.error('Gagal mengirim koleksi')
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit: err instanceof Error ? err.message : 'Terjadi kesalahan',
        })
        return
      }

      formApi.reset()
      onSuccess()
    },
  })

  useEffect(() => {
    void form.validateAllFields('change')
  }, [])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const childIds = useStore(form.store, (state) => state.values.childIds)

  const filteredRecipients = searchTerm
    ? recipients.filter((r) => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : recipients

  const hasUnsavedChanges = JSON.stringify([...childIds].sort()) !== JSON.stringify([...initialRecipients].sort())

  const isPilihSemua =
    childIds.length === filteredRecipients.length &&
    filteredRecipients.length > 0 &&
    filteredRecipients.every((r) => childIds.includes(r.id))

  const handleSelectAll = () => {
    if (isPilihSemua) {
      form.setFieldValue('childIds', [])
    } else {
      form.setFieldValue(
        'childIds',
        filteredRecipients.map((r) => r.id),
      )
    }
  }

  if (isPendingChildren) {
    return (
      <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-4'>
        <Skeleton className='srd:h-8 srd:w-full' />
        <div className='srd:flex srd:max-h-[50vh] srd:w-full srd:flex-col srd:gap-y-2'>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className='srd:h-14 srd:w-full' />
          ))}
        </div>
      </div>
    )
  }

  if (isErrorChildren) {
    return (
      <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:gap-y-3'>
        <p className='srd:text-sm srd:text-destructive'>Gagal memuat daftar anak. Silakan coba lagi.</p>
        <Button size='sm' variant='outline' onClick={() => void refetchChildren()}>
          Coba lagi
        </Button>
      </div>
    )
  }

  // No children in the whole school — replace the form with the add-child empty state.
  // When a class filter is active we keep the form so the user can switch classes.
  if (recipients.length === 0 && teamId === null) {
    return (
      <div className='srd:flex srd:flex-col srd:gap-y-2'>
        <p className='srd:text-sm srd:text-muted-foreground'>
          Tidak ada penerima untuk dipilih. Tambahkan profil anak terlebih dahulu.
        </p>
        <Button variant='outline' asChild>
          {renderMakeNewChildLink({ children: 'Tambahkan anak' })}
        </Button>
      </div>
    )
  }

  return (
    <>
      <form
        id='sent-collection-to-child-form'
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
        className='srd:flex srd:min-w-0 srd:w-full srd:flex-col srd:gap-y-4'
      >
        <header className='srd:flex srd:w-full srd:flex-col srd:gap-y-2'>
          <p className='srd:text-sm'>Menampilkan {filteredRecipients.length} nama</p>

          <SchoolClassSelect value={teamId} onValueChange={setTeamId} size='sm' showNoClass />

          <InputGroup className='srd:h-8'>
            <InputGroupAddon align='inline-start'>
              <Search className='srd:size-4 srd:text-main-blue-100' />
            </InputGroupAddon>
            <InputGroupInput
              placeholder='Cari berdasarkan nama anak'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              type='search'
            />
          </InputGroup>

          <div className='srd:flex srd:items-center srd:justify-end'>
            <Button
              type='button'
              variant='link'
              className={cn(!isPilihSemua ? 'srd:text-main-blue-100' : 'srd:text-main-red-100')}
              onClick={handleSelectAll}
            >
              {isPilihSemua ? 'Batal pilih semua' : 'Pilih semua'}
            </Button>
          </div>
        </header>

        <FieldSet
          className='srd:flex srd:min-w-0 srd:max-h-[50vh] srd:w-full srd:flex-col srd:gap-y-4 srd:overflow-x-hidden srd:overflow-y-auto'
          disabled={isSubmitting}
        >
          {filteredRecipients.length === 0 && (
            <p className='srd:text-sm srd:text-muted-foreground srd:py-4 srd:text-center'>
              {searchTerm ? 'Tidak ada anak yang cocok dengan pencarian.' : 'Tidak ada anak di kelas ini.'}
            </p>
          )}
          <form.Field name='childIds'>
            {(field) =>
              filteredRecipients.map((recipient) => {
                const checked = (field.state.value.includes(recipient.id) as boolean | undefined) ?? false
                return (
                  <label
                    key={recipient.id}
                    className={cn(
                      'srd:flex srd:min-w-0 srd:w-full srd:cursor-pointer srd:select-none srd:items-center srd:justify-between srd:gap-x-4 srd:rounded-lg srd:p-2',
                      checked && 'srd:bg-main-blue-20',
                    )}
                  >
                    <div className='srd:flex srd:min-w-0 srd:w-full srd:items-center srd:gap-x-2'>
                      <Avatar profile='user' className='srd:size-10 srd:shrink-0'>
                        <AvatarImage src={recipient.image ?? undefined} alt={recipient.name} />
                        <AvatarFallback>{getInitials(recipient.name)}</AvatarFallback>
                      </Avatar>
                      <p className='srd:min-w-0 srd:truncate srd:text-base srd:font-semibold sm:srd:text-lg'>
                        {recipient.name}
                      </p>
                    </div>
                    <Checkbox
                      className='srd:shrink-0'
                      checked={checked}
                      onCheckedChange={(value) => {
                        const current = field.state.value
                        const next = value ? [...current, recipient.id] : current.filter((id) => id !== recipient.id)
                        field.handleChange(next)
                      }}
                    />
                  </label>
                )
              })
            }
          </form.Field>
        </FieldSet>

        {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' && (
          <p className='srd:text-xs srd:text-destructive srd:mt-2'>{errorMap.onSubmit}</p>
        )}

        <div className='srd:mt-4 srd:flex srd:w-full srd:items-center srd:justify-end srd:gap-x-4'>
          <Button
            type='button'
            variant='link'
            className='srd:text-main-blue-100'
            onClick={() => (withAreYouSureDialog && hasUnsavedChanges ? setOpenAreYouSure(true) : onCancel())}
            disabled={isSubmitting}
          >
            Batal
          </Button>
          <Button
            type='submit'
            className='srd:rounded-full srd:bg-main-blue-100 srd:font-medium'
            disabled={!canSubmit || childIds.length === 0}
            loading={isSubmitting}
          >
            Kirim koleksi
          </Button>
        </div>
      </form>

      {withAreYouSureDialog && (
        <AreYouSureDialog
          open={openAreYouSure}
          onOpenChange={setOpenAreYouSure}
          title='Batalkan pengiriman koleksi?'
          description='Pilihan penerima yang sudah dipilih akan dibatalkan.'
          onConfirm={() => {
            form.reset()
            setOpenAreYouSure(false)
            onCancel()
          }}
        />
      )}
    </>
  )
}
